import { PlayerEventType } from "amazon-ivs-player";
import videojs from "video.js";

import type { AWSIVSPlayer } from "../videojsPlayer";
import type { IMenuButton, IMenuItem } from "./type";
import type { Player as IVSPlayer, Quality } from "amazon-ivs-player";
import type Player from "video.js/dist/types/player";

export const getSettingMenuButton = () => {
  const VideoJsMenuButton = videojs.getComponent("MenuButton"); // as unknown as MenuButton;
  const VideoJsMenuItem = videojs.getComponent("MenuItem"); // as unknown as MenuItem;
  const VideoJsMenu = videojs.getComponent("Menu");

  type IVSQualityMenuItemOption = {
    selected: boolean;
    ivsPlayer: IVSPlayer;
    parentButton: IVSSettingsButton;
  } & (
    | {
        type: "manual";
        quality: Quality;
      }
    | {
        type: "auto";
        quality: null;
      }
  );

  /**
   * 【サブメニュー項目】画質選択用のアイテム
   */
  class IVSQualityMenuItem extends (VideoJsMenuItem as unknown as IMenuItem) {
    private ivsPlayer: IVSPlayer;
    private quality: Quality | null;
    private parentButton: IVSSettingsButton;

    static AutoLabel = "Auto";

    constructor(player: Player, options: IVSQualityMenuItemOption) {
      super(player, {
        label:
          options.type === "auto"
            ? IVSQualityMenuItem.AutoLabel
            : options.quality.name || `${options.quality.height}p`,
        selectable: true,
        selected: options.selected || false,
      });

      this.ivsPlayer = options.ivsPlayer;
      this.quality = options.quality;
      this.parentButton = options.parentButton;
    }

    /**
     * クリックされた時の動作
     */
    handleClick(event: Event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (this.options(undefined).label === IVSQualityMenuItem.AutoLabel) {
        console.log("Switching to Auto Mode");
        this.ivsPlayer.setAutoQualityMode(true);
      } else if (this.quality) {
        this.ivsPlayer.setQuality(this.quality);
      }

      this.parentButton.changeMenuState("main");

      // 画質が変わったら、ラベル("画質: 720p"など)を更新するために再描画
      const handler = () => {
        console.log("EventListener PlayerEventType.QUALITY_CHANGED");
        this.ivsPlayer.removeEventListener(
          PlayerEventType.QUALITY_CHANGED,
          handler
        );

        this.parentButton.update(); // createItemsを再実行させる

        // メニューを開いた状態に固定（vjs-lock-showingクラスを維持）
        this.parentButton.pressButton();
      };
      this.ivsPlayer.addEventListener(PlayerEventType.QUALITY_CHANGED, handler);
    }
  }

  type ToggleLLMenuItemOptions = {
    label: string;
    useLL: boolean;
    toggleLowLatency: () => void;
    parentButton: IVSSettingsButton;
  };

  /**
   * 「低遅延」ボタン
   */
  class ToggleLLMenuItem extends (VideoJsMenuItem as unknown as IMenuItem) {
    private parentButton: IVSSettingsButton;

    constructor(player: Player, options: ToggleLLMenuItemOptions) {
      super(player, options as never);
      this.parentButton = options.parentButton;
    }

    createEl() {
      const el = super.createEl();
      const textEl = el.querySelector(".vjs-menu-item-text");

      if (textEl) {
        textEl.textContent = ""; // デフォルトのテキストをクリア

        const labelSpan = document.createElement("span");
        labelSpan.className = "vjs-settings-label";
        labelSpan.textContent = (
          this.options(undefined) as ToggleLLMenuItemOptions
        ).label;
        textEl.appendChild(labelSpan);

        const valueSpan = document.createElement("span");
        valueSpan.className = "vjs-settings-value";
        const { useLL } = this.options(undefined) as ToggleLLMenuItemOptions;
        valueSpan.innerHTML = `
        <style>
        .vjs-menu-item .vjs-settings-value .switch {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 17px;
        }
        .vjs-menu-item .vjs-settings-value .switch input {
          opacity: 0;
          width: 0;
          height: 0;
          pointer-events: none;
        }

        .vjs-menu-item .vjs-settings-value .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
        }
        .vjs-menu-item .vjs-settings-value .slider:before {
          position: absolute;
          content: "";
          height: 10px;
          width: 10px;
          left: 3px;
          bottom: 3px;
          background-color: white;
        }
        .vjs-menu-item .vjs-settings-value input:checked + .slider:before {
          transform: translateX(23px);
        }
        .vjs-menu-item .vjs-settings-value input:checked + .slider {
          background-color: #2196F3;
        }
        .vjs-menu-item .vjs-settings-value input:focus + .slider {
          box-shadow: 0 0 1px #2196F3;
        }

        .vjs-menu-item .vjs-settings-value .slider.round {
          border-radius: 17px;
        }
        .vjs-menu-item .vjs-settings-value .slider.round:before {
          border-radius: 50%;
        }
        </style>

        <label class="switch">
          <input type="checkbox" ${useLL ? "checked" : ""} tabindex="-1" aria-hidden="true"/>
          <span class="slider round"></span>
        </label>
      `;
        textEl.appendChild(valueSpan);
      }

      return el;
    }

    handleClick(e: Event) {
      e.preventDefault();
      e.stopImmediatePropagation();

      // 親のコールバックを呼んで状態を更新
      (this.options(undefined) as ToggleLLMenuItemOptions).toggleLowLatency();

      // 親ボタンのupdateを呼んでメニュー全体を再描画させる
      this.parentButton.update();

      // メニューを開いたままにする
      this.parentButton.pressButton();
    }
  }

  type BackMenuItemOptions = {
    parentButton: IVSSettingsButton;
  };

  /**
   * 【ナビ用アイテム】「< 戻る」ボタン
   */
  class BackMenuItem extends (VideoJsMenuItem as unknown as IMenuItem) {
    private parentButton: IVSSettingsButton;

    constructor(player: Player, options: BackMenuItemOptions) {
      super(player, {
        label: "< 戻る",
        selectable: false,
      });
      this.parentButton = options.parentButton;
    }

    handleClick(event: Event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      // メインメニューに戻る
      this.parentButton.changeMenuState("main");
    }
  }

  type QualityNavigatorItemOptions = {
    currentStatusLabel: string;
    parentButton: IVSSettingsButton;
  };

  /**
   * 【ナビ用アイテム】メインメニューの「画質 >」ボタン
   */
  class QualityNavigatorItem extends (VideoJsMenuItem as unknown as IMenuItem) {
    private parentButton: IVSSettingsButton;

    constructor(player: Player, options: QualityNavigatorItemOptions) {
      super(player, {
        ...options,
        selectable: false,
      });

      this.parentButton = options.parentButton;
    }

    createEl() {
      const el = super.createEl();
      const textEl = el.querySelector(".vjs-menu-item-text");

      if (textEl) {
        textEl.textContent = "";

        const labelSpan = document.createElement("span");
        labelSpan.className = "vjs-settings-label";
        labelSpan.textContent = "画質";
        textEl.appendChild(labelSpan);

        const valueSpan = document.createElement("span");
        valueSpan.className = "vjs-settings-value";
        const { currentStatusLabel } = this.options(
          undefined
        ) as QualityNavigatorItemOptions;
        valueSpan.textContent = currentStatusLabel;
        textEl.appendChild(valueSpan);
      }

      return el;
    }

    handleClick(event: Event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      this.parentButton.changeMenuState("quality");
    }
  }

  type IVSSettingsButtonOptions = {
    hasLL: boolean;
    useLL: boolean;
    toggleLowLatency: () => void;
  };

  /**
   * 【親コンポーネント】設定ボタン本体
   */
  class IVSSettingsButton extends (VideoJsMenuButton as unknown as IMenuButton) {
    public menu!: InstanceType<typeof VideoJsMenu>;

    private ivsPlayer: IVSPlayer;
    // 表示状態管理
    private menuState: "main" | "quality";

    constructor(player: Player, options: IVSSettingsButtonOptions) {
      super(player, options as never);

      this.menuState = "main";
      this.ivsPlayer = (player as AWSIVSPlayer).getIVSPlayer();

      this.ivsPlayer.addEventListener(PlayerEventType.INITIALIZED, () => {
        this.show();
        this.update();
      });
      document.addEventListener("click", this.handleOutsideClick);
      this.on("dispose", () => {
        document.removeEventListener("click", this.handleOutsideClick);
      });
    }

    /**
     * ボタンの見た目（CSSクラス）
     * vjs-icon-cog は歯車アイコン、vjs-icon-hd はHDアイコンです。
     */
    buildCSSClass() {
      return `vjs-ivs-settings-button vjs-icon-cog ${super.buildCSSClass()}`;
    }

    changeMenuState(menuState: "main" | "quality") {
      this.menuState = menuState;

      this.update();

      const menuEl = this.menu.el();
      if (menuState === "quality") {
        menuEl.classList.add("vjs-settings-menu-quality");
      } else {
        menuEl.classList.remove("vjs-settings-menu-quality");
      }

      // メニューを開いた状態に固定（vjs-lock-showingクラスを維持）
      this.pressButton();
    }

    //外側クリックで閉じるための処理を追加
    handleOutsideClick = (event: MouseEvent) => {
      const menuEl = this.menu?.el();
      if (!menuEl) {
        return;
      }
      const isOpen = !menuEl.classList.contains("vjs-hidden");
      if (!isOpen) {
        return;
      }

      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      if (!menuEl.contains(target) && !this.el().contains(target)) {
        this.unpressButton();
      }
    };

    createItems() {
      switch (this.menuState) {
        case "main":
          return this.createMainItems();
        case "quality":
          return this.createQualityItems();
      }
    }

    // メインメニューの中身を作る
    createMainItems() {
      const items = [];

      const header = new (VideoJsMenuItem as unknown as IMenuItem)(
        this.player(),
        {
          label: "設定",
          selectable: false,
          className: "vjs-settings-menu-header",
        }
      );
      header.disable();
      items.push(header);

      // 現在の画質名を取得してラベルにする
      let statusLabel = "不明";
      if (this.ivsPlayer.isAutoQualityMode()) {
        statusLabel = "Auto";
      } else {
        const q = this.ivsPlayer.getQuality();
        statusLabel = q ? q.name : "-";
      }

      items.push(
        new QualityNavigatorItem(this.player(), {
          currentStatusLabel: statusLabel,
          parentButton: this,
        })
      );

      // 将来的に「再生速度」などを追加する場合はここにpushする

      // 低遅延が有効なLiveでのみ「低遅延」メニューを追加
      if ((this.options(undefined) as IVSSettingsButtonOptions).hasLL) {
        items.push(
          new ToggleLLMenuItem(this.player(), {
            label: "低遅延",
            useLL: (this.options(undefined) as IVSSettingsButtonOptions).useLL,
            toggleLowLatency: (
              this.options(undefined) as IVSSettingsButtonOptions
            ).toggleLowLatency,
            parentButton: this,
          })
        );
      }

      return items;
    }

    // 画質リスト画面の中身を作る
    createQualityItems() {
      const items = [];
      const qualities = this.ivsPlayer.getQualities();
      const isAutoMode = this.ivsPlayer.isAutoQualityMode();

      // 1. 戻るボタン
      items.push(
        new BackMenuItem(this.player(), {
          parentButton: this,
        })
      );

      // 2. Autoボタン (常に表示)
      items.push(
        new IVSQualityMenuItem(this.player(), {
          type: "auto",
          quality: null,
          // qualitiesがない場合はisAutoModeがfalseでもAutoを選択状態にする
          selected: isAutoMode || !qualities || qualities.length === 0,
          ivsPlayer: this.ivsPlayer,
          parentButton: this,
        })
      );

      // 3. 各画質ボタン (qualitiesが存在する場合のみ)
      if (qualities && qualities.length > 0) {
        // ソート (高い画質順)
        qualities.sort((a, b) => b.height - a.height);

        const currentQuality = this.ivsPlayer.getQuality();
        console.log("Current Quality:", currentQuality);

        qualities.forEach((q) => {
          const isSelected =
            !isAutoMode && currentQuality && q.name === currentQuality.name;
          items.push(
            new IVSQualityMenuItem(this.player(), {
              type: "manual",
              quality: q,
              selected: isSelected,
              ivsPlayer: this.ivsPlayer,
              parentButton: this,
            })
          );
        });
      }

      return items;
    }
  }

  return {
    IVSQualityMenuItem,
    BackMenuItem,
    QualityNavigatorItem,
    IVSSettingsButton,
  };
};
